import React, { useState, useEffect, useCallback } from "react";
import ProductModal from "../../components/admin/ProductModal.tsx";
import api from "../../services/api.tsx";
import { useError } from "../../contexts/ErrorContext.tsx";
import { useCart } from "../../contexts/CartContext.tsx";

const emptyProduct = {
  name: "",
  description: "",
  brand: "",
  category: "",
  price: 0,
  stock: 0,
  technicalDetails: "",
  image: null as File | null,
};

const ProductManagementPage = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [newProduct, setNewProduct] = useState(emptyProduct);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editProduct, setEditProduct] = useState({ ...emptyProduct });
  const { setError } = useError();
  const { fetchCart } = useCart();

  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await api.get("/api/product", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data);
    } catch (err) {
      setError(err.response?.data?.Message || "Bir hata oluştu.");
    }
  }, [setError]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("name", newProduct.name);
    formData.append("description", newProduct.description);
    formData.append("brand", newProduct.brand);
    formData.append("category", newProduct.category);
    formData.append("price", newProduct.price.toString());
    formData.append("stock", newProduct.stock.toString());
    formData.append("technicalDetails", newProduct.technicalDetails || "");

    if (newProduct.image) {
      formData.append("image", newProduct.image);
    } else {
      alert("Resim eklenemedi.");
    }

    try {
      const token = localStorage.getItem("access_token");
      await api.post("/api/product", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setNewProduct({ ...emptyProduct });
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      alert("Ürün eklenemedi.");
      setError(err.response?.data?.Message || "Bir hata oluştu.");
    }
  };

  // DELETE PRODUCT
  const handleDelete = async (id: string) => {
    if (!window.confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    try {
      const token = localStorage.getItem("access_token");
      await api.delete(`/api/product/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchProducts();
      await fetchCart();
    } catch (err) {
      alert("Ürün silinemedi.");
      setError(err.response?.data?.Message || "Bir hata oluştu.");
    }
  };

  // DÜZENLEME MODALINI AÇ
  const startEdit = (product: any) => {
    setEditingId(product.id);
    setEditProduct({
      ...product,
      image: null, // resmi güncellemeyecekseniz null bırakın
    });
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditProduct({ ...editProduct, [e.target.name]: e.target.value });
  };

  // DÜZENLEME MODALINI KAPAT
  const closeEditModal = () => {
    setEditingId(null);
    setEditProduct({ ...emptyProduct });
  };

  // DÜZENLEMEYİ KAYDET
  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("access_token");
      await api.put(`/api/product/${editingId}`, editProduct, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closeEditModal();
      fetchProducts();
    } catch (err) {
      alert("Ürün güncellenemedi.");
      setError(err.response?.data?.Message || "Bir hata oluştu.");
    }
  };

  return (
    <div className="p-8">
      <button
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mb-4"
        onClick={() => setShowModal(true)}
      >
        Ürün Ekle
      </button>

      {/* ÜRÜN EKLEME MODALI */}
      <ProductModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleAdd}
        newProduct={newProduct}
        setNewProduct={setNewProduct}
        isEdit={false}
      />

      {/* ÜRÜN DÜZENLEME MODALI */}
      <ProductModal
        show={!!editingId}
        onClose={closeEditModal}
        onSubmit={handleEditSave}
        newProduct={editProduct}
        setNewProduct={setEditProduct}
        isEdit={true}
      />

      <table className="min-w-full bg-white border rounded text-sm">
        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="py-1 px-2 border-b">Adı</th>
            <th className="py-1 px-2 border-b">Açıklama</th>
            <th className="py-1 px-2 border-b">Marka</th>
            <th className="py-1 px-2 border-b">Kategori</th>
            <th className="py-1 px-2 border-b">Fiyat</th>
            <th className="py-1 px-2 border-b">Stok</th>
            <th className="py-1 px-2 border-b">Özellikler</th>
            <th className="py-1 px-2 border-b">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product: any) => (
            <tr key={product.id}>
              <td className="py-1 px-2 border-b">{product.name}</td>
              <td className="py-1 px-2 border-b">{product.description}</td>
              <td className="py-1 px-2 border-b">{product.brand}</td>
              <td className="py-1 px-2 border-b">{product.category}</td>
              <td className="py-1 px-2 border-b">{product.price} TL</td>
              <td className="py-1 px-2 border-b">{product.stock}</td>
              <td className="py-1 px-2 border-b">
                {product.technicalDetails ? product.technicalDetails.substring(0, 10) + "..." : ""}
              </td>
              <td className="py-1 px-2 border-b">
                <button
                  className="text-blue-600 hover:underline mr-2"
                  onClick={() => startEdit(product)}
                >
                  Düzenle
                </button>
                <button
                  className="text-red-600 hover:underline"
                  onClick={() => handleDelete(product.id)}
                >
                  Sil
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductManagementPage;